/**
 * Classification trust + Business/ITGC detect.
 *
 * Detect (when Classification missing or mapped column is not real classification):
 * - Business: Subprocess filled AND System empty
 * - ITGC:     Subprocess empty AND System filled
 * - else:     undecided (caller falls back to default = Business)
 */

import { normalizeGenericHeaderText } from '../genericRcmFields.js';

/**
 * @param {Record<string, number|string|null|undefined>|null|undefined} mapping
 * @returns {boolean}
 */
export function isClassificationColumnMapped(mapping) {
  const v = mapping?.classification;
  return v !== undefined && v !== null && v !== '';
}

/**
 * True when header is a real Classification header, or sample cells look like
 * classification labels (not Title / UID / random columns).
 *
 * @param {string} headerLabel
 * @param {string[]} sampleValues
 * @returns {boolean}
 */
export function isMappedClassificationColumnValid(headerLabel, sampleValues) {
  const h = normalizeGenericHeaderText(headerLabel);
  if (h === 'classification' || h === 'control classification') {
    return true;
  }

  const samples = (sampleValues || [])
    .map((s) => String(s ?? '').trim())
    .filter((s) => s !== '');
  if (samples.length === 0) return false;

  const classLike = samples.filter((s) => {
    const u = s.toUpperCase();
    return (
      u.includes('ITGC') ||
      u.includes('BUSINESS') ||
      /\bBP\b/.test(u) ||
      u.includes('BUSINESS PROCESS')
    );
  });

  return classLike.length >= Math.max(1, Math.ceil(samples.length * 0.3));
}

/**
 * @param {{ processName?: string, entityName?: string }} signals
 * @returns {'business' | 'itgc' | null}
 */
export function detectRcmRowCategory(signals) {
  const hasSubprocess = String(signals?.processName ?? '').trim() !== '';
  const hasSystem = String(signals?.entityName ?? '').trim() !== '';

  if (hasSubprocess && !hasSystem) return 'business';
  if (!hasSubprocess && hasSystem) return 'itgc';
  return null;
}

/**
 * Default Type rule: cell contains ITGC.
 * @param {string} classificationCell
 * @returns {boolean}
 */
export function isItgcFromClassificationCell(classificationCell) {
  return String(classificationCell ?? '')
    .toUpperCase()
    .includes('ITGC');
}

/**
 * Resolve Type when Classification is unmapped or mapped column failed the trust check.
 * - If at least one row can be detected → use detect; undecided rows → Business (default)
 * - If no row can be detected → fall back to classification-cell rule for every row
 *
 * @param {{ processName: string, entityName: string, classificationCell: string }[]} rowSignals
 * @returns {{ useDetect: boolean, isItgcByIndex: boolean[] }}
 */
export function resolveTypesWhenClassificationUntrusted(rowSignals) {
  const list = Array.isArray(rowSignals) ? rowSignals : [];
  const detected = list.map((s) => detectRcmRowCategory(s));
  const anyDetected = detected.some((c) => c !== null);

  if (!anyDetected) {
    return {
      useDetect: false,
      isItgcByIndex: list.map((s) => isItgcFromClassificationCell(s.classificationCell)),
    };
  }

  return {
    useDetect: true,
    isItgcByIndex: detected.map((c) => (c === null ? false : c === 'itgc')),
  };
}
